import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CanActivateFn, CanMatchFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';
import { USER_ROLES } from '../models/auth.models';
import { MonoovaService } from '../../services/monoova.service';

/**
 * Authentication Guard (Functional)
 * Protects routes by checking if user is authenticated
 * Uses modern Angular functional guard approach
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  // Check if user is authenticated
  const isAuth = tokenStorage.isAuthenticated();
  console.log('authGuard: Checking authentication', {
    isAuthenticated: isAuth,
    path: state.url,
    hasToken: !!tokenStorage.getTokenInfo(),
    hasUser: !!tokenStorage.getUser(),
    hasSubscription: tokenStorage.getSubscriptionStatus()
  });

  if (isAuth) {
    console.log('authGuard: Access granted to', state.url);
    return true;
  }

  console.warn('authGuard: Access denied. Redirecting to sign-in from', state.url);
  
  // Store the attempted URL for redirecting after login
  tokenStorage.setRedirectUrl(state.url);

  // Redirect to login (using sign-in path)
  router.navigate(['/sign-in']);
  return false;
};

/**
 * Guest Guard (Functional)
 * Prevents authenticated users from accessing guest-only routes (like login)
 */
// export const guestGuard: CanActivateFn = (): Observable<boolean> | boolean => {
//   const authService = inject(AuthService);
//   const tokenStorage = inject(TokenStorageService);
//   const router = inject(Router);

//   // If user is authenticated, redirect to dashboard
//   if (tokenStorage.isAuthenticated()) {
//     router.navigate(['/dashboard']);
//     return false;
//   }

//   return true;
// };

export const guestGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  // Check if this is an add-venue flow
  const isAddVenueFlow = route.queryParams['addVenue'] === 'true';
  const hasReferralCode = !!route.queryParams['code'];
  const isPaymentOptionsRoute =
    state.url.split('?')[0] === '/sign-up/payment-options';

  // If user is authenticated and NOT in add-venue flow, redirect to dashboard
  if (
    tokenStorage.isAuthenticated() &&
    !isAddVenueFlow &&
    !hasReferralCode &&
    !isPaymentOptionsRoute
  ) {
    router.navigate(['/dashboard']);
    return false;
  }

  return true;
};

/**
 * Admin Guard (Functional)
 * Protects admin routes by checking authentication and admin role
 */
export const adminGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  // First check authentication
  if (!tokenStorage.isAuthenticated()) {
    tokenStorage.setRedirectUrl(state.url);
    router.navigate(['/sign-in']);
    return false;
  }

  // Check admin role
  return authService.user$.pipe(
    take(1),
    map(user => {
      if (user && authService.hasRole(USER_ROLES.ADMIN)) {
        return true;
      } else {
        router.navigate(['/unauthorized']);
        return false;
      }
    })
  );
};

/**
 * Role Guard Factory (Functional)
 * Creates a guard that checks for specific roles
 */
export const createRoleGuard = (requiredRoles: string[]): CanActivateFn => {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean => {
    const authService = inject(AuthService);
    const tokenStorage = inject(TokenStorageService);
    const router = inject(Router);

    // First check authentication
    if (!tokenStorage.isAuthenticated()) {
      tokenStorage.setRedirectUrl(state.url);
      router.navigate(['/sign-in']);
      return false;
    }

    // Check required roles
    return authService.user$.pipe(
      take(1),
      map(user => {
        if (user && authService.hasAnyRole(requiredRoles)) {
          return true;
        } else {
          router.navigate(['/unauthorized']);
          return false;
        }
      })
    );
  };
};

/**
 * Route Data Guard (Functional)
 * Checks roles specified in route data
 */
export const routeDataGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  // First check authentication
  if (!tokenStorage.isAuthenticated()) {
    tokenStorage.setRedirectUrl(state.url);
    router.navigate(['/sign-in']);
    return false;
  }

  // Get required roles from route data
  const requiredRoles = route.data['roles'] as string[];
  
  if (!requiredRoles || requiredRoles.length === 0) {
    return true; // No specific roles required
  }

  // Check required roles
  return authService.user$.pipe(
    take(1),
    map(user => {
      if (user && authService.hasAnyRole(requiredRoles)) {
        return true;
      } else {
        router.navigate(['/unauthorized']);
        return false;
      }
    })
  );
};

/**
 * Subscription Guard
 * Redirects to insights if the user's subscription is not active.
 * Only applies to OrgAdmin / roleId-4 users; all others are allowed through.
 */

/* export const subscriptionGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean => {
  const monovaService = inject(MonoovaService);
  const router = inject(Router);

  if (monovaService.isSubscriptionActive()) {
    return true;
  }

  router.navigate(['/insights']);
  return false;
}; */

export const subscriptionGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean => {
  const monovaService = inject(MonoovaService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const state = authService.getCurrentAuthState();
  const isSuperAdmin =
    state.user?.roleId === 6 ||
    (state.user?.roles?.includes('SuperAdmin') ?? false);

  if (isSuperAdmin) {
    return true;
  }

  if (monovaService.isSubscriptionActive()) {
    return true;
  }

  router.navigate(['/sign-up/payment-options']);
  return false;
};

/**
 * Can Match Guard for lazy loading
 * Prevents loading modules if user doesn't have access
 */
export const authCanMatch: CanMatchFn = (): Observable<boolean> | boolean => {
  const tokenStorage = inject(TokenStorageService);
  return tokenStorage.isAuthenticated();
};

/**
 * Admin Can Match Guard for lazy loading
 */
export const adminCanMatch: CanMatchFn = (): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);

  if (!tokenStorage.isAuthenticated()) {
    return false;
  }

  return authService.user$.pipe(
    take(1),
    map(user => user ? authService.hasRole(USER_ROLES.ADMIN) : false)
  );
};

// Utility functions for guards
export const AuthGuardUtils = {
  /**
   * Store redirect URL for post-login navigation
   */
  setRedirectUrl: (url: string): void => {
    const tokenStorage = inject(TokenStorageService);
    tokenStorage.setRedirectUrl(url);
  },

  /**
   * Get and clear stored redirect URL
   */
  getAndClearRedirectUrl: (): string | null => {
    const tokenStorage = inject(TokenStorageService);
    return tokenStorage.getAndClearRedirectUrl();
  },

  /**
   * Check if user has permission for route
   */
  hasRoutePermission: (requiredRoles: string[]): Observable<boolean> => {
    const authService = inject(AuthService);
    return authService.user$.pipe(
      take(1),
      map(user => user ? authService.hasAnyRole(requiredRoles) : false)
    );
  }
};

export const superAdminGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const state = authService.getCurrentAuthState();
  const isSuperAdmin =
    state.user?.roleId === 6 ||
    (state.user?.roles?.includes('SuperAdmin') ?? false);

  if (isSuperAdmin) {
    return true;
  }

  router.navigate(['/dashboard']);
  return false;
};